import {
  detectFileCategory,
  isSupportedPreview,
  FILE_CATEGORIES
} from "../utility/fileTypeUtils"

// Status do attachment
export const ATTACHMENT_STATUS = {
  PENDING: "pending",
  UPLOADING: "uploading",
  UPLOADED: "uploaded",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed"
}

// Tipos de attachment
export const ATTACHMENT_TYPES = {
  IMAGE: "image",
  DOCUMENT: "document",
  MODEL_3D: "model3d",
  ARCHIVE: "archive",
  VIDEO: "video",
  AUDIO: "audio"
}

export class Attachment {
  constructor() {
    this.id = 0
    this.caseId = 0
    this.userId = 0
    this.bucketName = ""
    this.fileName = ""
    this.originalName = ""
    this.fileSize = 0
    this.mimeType = ""
    this.extension = ""
    this.status = ATTACHMENT_STATUS.PENDING
    this.type = ATTACHMENT_TYPES.DOCUMENT
    this.uploadPercent = 0
    this.url = ""
    this.thumbnailUrl = ""
    this.metadata = {}
    this.isPublic = false
    this.downloadCount = 0
    this.createdAt = null
    this.updatedAt = null
    this.expiresAt = null

    // Dados voláteis para upload
    this.file = null
    this.uploadId = null
    this.presignedUrl = null
  }

  static FromObj(obj) {
    const instance = new Attachment()

    instance.id = obj.id || 0
    instance.caseId = obj.caseId || 0
    instance.userId = obj.userId || 0
    instance.bucketName = obj.bucketName || ""
    instance.fileName = obj.fileName || ""
    instance.originalName = obj.originalName || ""
    instance.fileSize = obj.fileSize || 0
    instance.mimeType = obj.mimeType || ""
    instance.extension = obj.extension || ""
    instance.status = obj.status || ATTACHMENT_STATUS.PENDING
    instance.type = obj.type || ATTACHMENT_TYPES.DOCUMENT
    instance.uploadPercent = obj.uploadPercent || 0
    instance.url = obj.url || ""
    instance.thumbnailUrl = obj.thumbnailUrl || ""
    instance.metadata = obj.metadata || {}
    instance.isPublic = obj.isPublic || false
    instance.downloadCount = obj.downloadCount || 0
    instance.createdAt = obj.createdAt || null
    instance.updatedAt = obj.updatedAt || null
    instance.expiresAt = obj.expiresAt || null

    return instance
  }

  // Identificador único
  string_id() {
    return `${this.bucketName}_${this.id}`
  }

  // Verificações de status
  isPending() {
    return this.status === ATTACHMENT_STATUS.PENDING
  }

  isUploading() {
    return this.status === ATTACHMENT_STATUS.UPLOADING
  }

  isUploaded() {
    return this.status === ATTACHMENT_STATUS.UPLOADED
  }

  isProcessing() {
    return this.status === ATTACHMENT_STATUS.PROCESSING
  }

  isCompleted() {
    return this.status === ATTACHMENT_STATUS.COMPLETED
  }

  isFailed() {
    return this.status === ATTACHMENT_STATUS.FAILED
  }

  // Verificações de tipo
  isImage() {
    return (
      this.type === ATTACHMENT_TYPES.IMAGE || this.mimeType.startsWith("image/")
    )
  }

  isVideo() {
    return (
      this.type === ATTACHMENT_TYPES.VIDEO || this.mimeType.startsWith("video/")
    )
  }

  isAudio() {
    return (
      this.type === ATTACHMENT_TYPES.AUDIO || this.mimeType.startsWith("audio/")
    )
  }

  isPdf() {
    const category = detectFileCategory(this.fileName, this.mimeType)
    return category === FILE_CATEGORIES.PDF
  }

  isDocx() {
    const category = detectFileCategory(this.fileName, this.mimeType)
    return category === FILE_CATEGORIES.DOCUMENT_DOCX
  }

  isSpreadsheet() {
    const category = detectFileCategory(this.fileName, this.mimeType)
    return category === FILE_CATEGORIES.DOCUMENT_XLSX
  }

  isText() {
    const category = detectFileCategory(this.fileName, this.mimeType)
    return category === FILE_CATEGORIES.TEXT
  }

  isDicom() {
    const category = detectFileCategory(this.fileName, this.mimeType)
    return category === FILE_CATEGORIES.DICOM
  }

  isDocument() {
    return this.type === ATTACHMENT_TYPES.DOCUMENT
  }

  is3DModel() {
    return this.type === ATTACHMENT_TYPES.MODEL_3D
  }

  isArchive() {
    return this.type === ATTACHMENT_TYPES.ARCHIVE
  }

  // Formatação
  getDisplayName() {
    return this.originalName || this.fileName
  }

  getFormattedSize() {
    const bytes = this.fileSize

    if (bytes === 0) return "0 Bytes"

    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  getStatusLabel() {
    const labels = {
      [ATTACHMENT_STATUS.PENDING]: "Pendente",
      [ATTACHMENT_STATUS.UPLOADING]: "Enviando",
      [ATTACHMENT_STATUS.UPLOADED]: "Enviado",
      [ATTACHMENT_STATUS.PROCESSING]: "Processando",
      [ATTACHMENT_STATUS.COMPLETED]: "Concluído",
      [ATTACHMENT_STATUS.FAILED]: "Falhou"
    }

    return labels[this.status] || "Desconhecido"
  }

  getStatusColor() {
    const colors = {
      [ATTACHMENT_STATUS.PENDING]: "warning",
      [ATTACHMENT_STATUS.UPLOADING]: "info",
      [ATTACHMENT_STATUS.UPLOADED]: "success",
      [ATTACHMENT_STATUS.PROCESSING]: "primary",
      [ATTACHMENT_STATUS.COMPLETED]: "success",
      [ATTACHMENT_STATUS.FAILED]: "danger"
    }

    return colors[this.status] || "secondary"
  }

  getTypeIcon() {
    const icons = {
      [ATTACHMENT_TYPES.IMAGE]: "Image",
      [ATTACHMENT_TYPES.VIDEO]: "Video",
      [ATTACHMENT_TYPES.AUDIO]: "Volume2",
      [ATTACHMENT_TYPES.DOCUMENT]: "FileText",
      [ATTACHMENT_TYPES.MODEL_3D]: "Box",
      [ATTACHMENT_TYPES.ARCHIVE]: "Archive"
    }

    return icons[this.type] || "File"
  }

  // Validações
  isValid() {
    return this.fileName.length > 0 && this.fileSize > 0
  }

  canDownload() {
    return this.isCompleted() && this.url
  }

  canPreview() {
    // Use centralized utility to check if preview is supported
    return isSupportedPreview({
      filename: this.fileName,
      mimetype: this.mimeType
    })
  }

  hasPreview() {
    return this.thumbnailUrl || this.canPreview()
  }

  // Métodos utilitários
  getDownloadUrl() {
    return this.url
  }

  getPreviewUrl() {
    return this.thumbnailUrl || (this.canPreview() ? this.url : null)
  }

  isExpired() {
    if (!this.expiresAt) return false

    return new Date() > new Date(this.expiresAt)
  }

  getDaysUntilExpiry() {
    if (!this.expiresAt) return null

    const now = new Date()
    const expiry = new Date(this.expiresAt)
    const timeDiff = expiry.getTime() - now.getTime()
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))

    return daysDiff
  }

  // Metadata
  getMetadata(key) {
    return this.metadata[key] || null
  }

  setMetadata(key, value) {
    this.metadata[key] = value
  }

  hasMetadata(key) {
    return key in this.metadata
  }

  // Upload
  setFile(file) {
    this.file = file
    this.originalName = file.name
    this.fileSize = file.size
    this.mimeType = file.type
    this.extension = file.name.split(".").pop().toLowerCase()
    this.type = this.detectTypeFromFile(file)
  }

  detectTypeFromFile(file) {
    if (file.type.startsWith("image/")) return ATTACHMENT_TYPES.IMAGE
    if (file.type.startsWith("video/")) return ATTACHMENT_TYPES.VIDEO
    if (file.type.startsWith("audio/")) return ATTACHMENT_TYPES.AUDIO
    if (file.type.includes("zip") || file.type.includes("rar"))
      return ATTACHMENT_TYPES.ARCHIVE
    if (
      file.name.endsWith(".stl") ||
      file.name.endsWith(".obj") ||
      file.name.endsWith(".ply")
    )
      return ATTACHMENT_TYPES.MODEL_3D

    return ATTACHMENT_TYPES.DOCUMENT
  }

  updateUploadProgress(percent) {
    this.uploadPercent = Math.min(100, Math.max(0, percent))

    if (this.uploadPercent === 100 && this.isPending()) {
      this.status = ATTACHMENT_STATUS.UPLOADED
    }
  }

  // Dados para formulário
  toFormData() {
    return {
      id: this.id,
      caseId: this.caseId,
      bucketName: this.bucketName,
      fileName: this.fileName,
      originalName: this.originalName,
      fileSize: this.fileSize,
      mimeType: this.mimeType,
      extension: this.extension,
      type: this.type,
      isPublic: this.isPublic,
      metadata: this.metadata
    }
  }
}

export class AttachmentUpload {
  constructor() {
    this.id = 0
    this.attachmentId = 0
    this.uploadId = ""
    this.presignedUrl = ""
    this.uploadFields = {}
    this.expiresAt = null
    this.completed = false
    this.multipart = false
    this.parts = []
  }

  static FromObj(obj) {
    const instance = new AttachmentUpload()

    instance.id = obj.id || 0
    instance.attachmentId = obj.attachmentId || 0
    instance.uploadId = obj.uploadId || ""
    instance.presignedUrl = obj.presignedUrl || ""
    instance.uploadFields = obj.uploadFields || {}
    instance.expiresAt = obj.expiresAt || null
    instance.completed = obj.completed || false
    instance.multipart = obj.multipart || false
    instance.parts = obj.parts || []

    return instance
  }

  isExpired() {
    if (!this.expiresAt) return false

    return new Date() > new Date(this.expiresAt)
  }

  isMultipart() {
    return this.multipart
  }

  isCompleted() {
    return this.completed
  }

  canUpload() {
    return !this.isExpired() && !this.isCompleted()
  }
}
